/**
 * Convert a local date + time string to a UTC Unix timestamp, taking into
 * account the IANA timezone of the business.
 *
 * Example:
 *   localTimeToUtcMs('2026-03-17', '09:00', 'America/Denver')
 *   → UTC ms for 2026-03-17T09:00:00 MDT (= 2026-03-17T15:00:00Z)
 *
 * Uses Intl.DateTimeFormat to determine the UTC offset for the given
 * date/time in the given timezone. Supported in V8 / CF Workers runtime,
 * and in every browser this package targets — so this is the one
 * implementation both the worker and the web client use, rather than each
 * maintaining its own local-time conversion and risking drift.
 *
 * @param dateStr  YYYY-MM-DD string
 * @param timeStr  HH:mm string (local time in the given timezone)
 * @param timezone IANA timezone string (e.g. 'America/New_York')
 * @returns Unix timestamp in milliseconds (UTC)
 */
export function localTimeToUtcMs(dateStr, timeStr, timezone) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hour, minute] = timeStr.split(':').map(Number);
    // Build a naive UTC date as if the local values were UTC — we'll correct below.
    const naiveUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    // Use Intl to find what the clock in the target timezone reads for this naive UTC instant.
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(new Date(naiveUtcMs));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const localYear = get('year');
    const localMonth = get('month') - 1;
    const localDay = get('day');
    const localHour = get('hour');
    const localMinute = get('minute');
    const localSecond = get('second');
    // Delta between what the formatter says the local time is vs what we actually want
    const localAsUtcMs = Date.UTC(localYear, localMonth, localDay, localHour, localMinute, localSecond);
    const offsetMs = naiveUtcMs - localAsUtcMs;
    // Correct the naive UTC instant by the offset to get the true UTC instant
    return naiveUtcMs + offsetMs;
}
//# sourceMappingURL=timezone.js.map